import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismNumberInput } from '@mylighthouse/prism-ember';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { task } from 'ember-concurrency';
import { notEq } from 'ember-truth-helpers';
import HotelChannelPricingEditCellPopover from 'lumen/components/hotel/channel-pricing/edit-cell-popover';
import HotelChannelPricingTablesCellsTableCellBase from 'lumen/components/hotel/channel-pricing/tables/cells/table-cell-base';
import HotelChannelPricingTablesCellsTableCellWrapper from 'lumen/components/hotel/channel-pricing/tables/cells/table-cell-wrapper';
import type {
  TdPmsTablePeriodColumnSignature,
  TdPmsTableRowSignature,
} from 'lumen/components/hotel/channel-pricing/tables/pms-table';
import type { Carate, Carecommendation } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { logPmsTablePricingCellDebugValues } from 'lumen/utils/channel-pricing/log-channels-pricing-cell-debug-values';
import {
  PMS_TABLE_PRICE_CELL_MODES,
  PMS_TABLE_ROW_TYPES,
} from 'lumen/utils/constants/channel-pricing-constants';
import {
  NUMBER_INPUT_MAX_VALUE,
  RECOMMENDATION_ACTION_PRICE_TYPES,
  RECOMMENDATION_ACTION_STATES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import { differenceInDays } from 'lumen/utils/dates/native-js-date-utils';
import { isDayInsideAutopilotLeadtimeRange } from 'lumen/utils/pricing-assistant/is-day-inside-autopilot-leadtime-range';
import { trackAutopilotDayToggle } from 'lumen/utils/pricing-assistant/track-autopilot-day-toggle';
import { generateRecommendationPushData } from 'lumen/utils/pricing-assistant/usage-analytics-utils';
import { timeout } from 'lumen/utils/waiters';

import styles from './pms-table-pricing-row-cell-content.module.css';

interface HotelChannelPricingTablesCellsPmsTablePricesPmsTablePricingRowCellContentSignature {
  Args: {
    column: TdPmsTablePeriodColumnSignature;
    isPricingChildRow: boolean;
    parentCellId: string;
    row: TdPmsTableRowSignature;
  };
}

type PricingRowActionButton = {
  action: (event: Event) => void;
  buttonType: 'primary' | 'secondary';
  dataTestId: string;
  disabled?: boolean;
  icon: IconName;
  name: string;
};

type ActiveCellMode =
  (typeof PMS_TABLE_PRICE_CELL_MODES)[keyof typeof PMS_TABLE_PRICE_CELL_MODES];

// These limits are used to determine if the new price is within the suggested range, so a warning can be shown if not
const NEW_PRICE_LOWER_LIMIT_FACTOR = 0.5;
const NEW_PRICE_UPPER_LIMIT_FACTOR = 1.5;

const SEGMENT_METADATA = {
  WARNING_MODALS: {
    ROOMTYPE_HIERARCHY_INCONSISTENCY: 'roomtype_hierarchy_inconsistency',
  },
} as const;

/**
 * Inconsistency in rate happens when our systems detect that one rate's value diverges
 * from the rates hierarchy defined in PMS settings.
 *
 * This can happen when users manually change a rate directly in the OTA, or PMS system.
 *
 * It is important to detect it and warn users about it, because pushing new values
 * in Smart Distribution, will override these values in the PMS and OTA systems.
 *
 * @param carate
 */
function checkInconsistencyInRate(carate: Carate | undefined): boolean {
  if (carate?.comparisonRates && carate.comparisonRates[0]) {
    return (
      carate.comparisonRates[0].value !== 0 &&
      carate.comparisonRates[0].difference_absolute !== 0 &&
      carate.comparisonRates[0].difference_relative !== 0
    );
  }

  return false;
}

export default class HotelChannelPricingTablesCellsPmsTablePricesPmsTablePricingRowCellContent extends Component<HotelChannelPricingTablesCellsPmsTablePricesPmsTablePricingRowCellContentSignature> {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service
  declare pricingAssistantHandleRecommendationActionsService: Services['pricing-assistant-handle-recommendation-actions-service'];
  @service
  declare pricingAssistantRecommendationActionPoller: Services['pricing-assistant-recommendation-action-poller'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked activeCellMode: ActiveCellMode = PMS_TABLE_PRICE_CELL_MODES.DEFAULT;
  @tracked editInputValue: number | null = this.editInputValueSameAsFormatted;
  @tracked isPopoverOpen = false;
  popoverElement: HTMLElement | null = null;

  debouncedUpdateEditInputValueTask = task(
    { restartable: true },
    async (enteredPrice): Promise<void> => {
      if (enteredPrice !== null) {
        await timeout();
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.editInputValue = enteredPrice;
    },
  );

  get areAutopilotActionsAvailable(): boolean {
    return Boolean(
      this.hotelAndMarketService.hotel.paAutopilotSettings?.['enabled'] &&
      !isDayInsideAutopilotLeadtimeRange(
        this.args.column.id,
        this.hotelAndMarketService.getLocalTime(),
        this.hotelAndMarketService.hotel.paAutopilotSettings,
      ),
    );
  }

  /*
    Note: In Pricing manager we've used the following getter on the carecommendation model:
     - formattedDifferenceBetweenRecommendationAndOwnRate
    That relies on the carecommendation -> carate to handle the comparison
    In the current Channel Pricing prototype we won't have the carate in the current endpoint response
    We need to compare with carecommendation.currentValue for now.
  */
  get arrowIconName(): 'arrow-down' | 'arrow-up' | undefined {
    if (
      !this.args.isPricingChildRow &&
      this.differenceBetweenRecommendationAndCurrentValue
    ) {
      if (this.differenceBetweenRecommendationAndCurrentValue < 0) {
        return 'arrow-down';
      }
      if (this.differenceBetweenRecommendationAndCurrentValue > 0) {
        return 'arrow-up';
      }
    }

    return undefined;
  }

  get carate(): Carate | undefined {
    return this.args.row.carates?.find(
      (carate) => carate.period.day.id === this.args.column.id,
    );
  }

  get currentValue(): number | undefined {
    return this.carate?.pmsValue;
  }

  get differenceBetweenRecommendationAndCurrentValue(): number | null {
    if (this.recommendation?.recommendedPmsValue && this.currentValue) {
      return this.recommendation.recommendedPmsValue - this.currentValue;
    }

    return null;
  }

  // SMART DISTRIBUTION TODO: follow up if it makes sense to share more code with edit-price-number-input or not.
  get editCellNewPriceInputWarningState(): {
    hasWarnings: boolean;
    warningMessage: string;
  } {
    // Case 1: Day with recommendation: Check against recommended price
    // Case 2: Day without recommendation: Check against current price
    const priceToCheck = this.editInputInitialValue;

    if (this.editInputValue) {
      // New price is lower than usual
      if (this.editInputValue < priceToCheck * NEW_PRICE_LOWER_LIMIT_FACTOR) {
        return {
          warningMessage: this.intl.t(
            'pricing_assistant.edit_price.warning.low',
          ),
          hasWarnings: true,
        };
      }

      // New price is higher than usual
      if (this.editInputValue > priceToCheck * NEW_PRICE_UPPER_LIMIT_FACTOR) {
        return {
          warningMessage: this.intl.t(
            'pricing_assistant.edit_price.warning.high',
          ),
          hasWarnings: true,
        };
      }
    }
    return {
      warningMessage: '',
      hasWarnings: false,
    };
  }

  get editInputInitialValue(): number {
    if (
      this.activeCellMode === PMS_TABLE_PRICE_CELL_MODES.UNDO &&
      this.carate
    ) {
      return this.carate.comparisonRates?.[0]?.value ?? 0;
    }

    if (this.recommendation && this.hasRecommendation) {
      return this.recommendation.recommendedPmsValue;
    }

    if (this.currentValue) {
      return this.currentValue;
    }

    return 0;
  }

  get editInputValueSameAsFormatted(): number {
    const formattedValueForInput = this.hotelAndMarketService.formatRate(
      this.editInputInitialValue,
      false,
      false,
      { maximumFractionDigits: 0, minimumFractionDigits: 0 },
    );
    return parseInt(formattedValueForInput as string, 10);
  }

  get hasActionPopover(): boolean {
    return (
      !this.access.isPricingAssistantActionOrInputDisabled &&
      !this.args.column.isPastDay &&
      !this.isInPushingState
    );
  }

  get hasCellRecommendation(): boolean {
    return (
      !this.showException &&
      this.hasRecommendation &&
      !this.isInPushingState &&
      !this.args.isPricingChildRow &&
      !this.args.column.isPastDay
    );
  }

  get hasCellRecommendationBackground(): boolean {
    return this.hasCellRecommendation && !this.isAutopilotEnabled;
  }

  get hasInconsistencyInRate(): boolean {
    if (!this.args.column.isPastDay) {
      return checkInconsistencyInRate(this.carate);
    }

    return false;
  }

  /**
   * This getter is used to detect if there's an inconsistent rate present for
   * the actioned day when a user tries to accept a recommendation in the PMS table
   */
  get hasInconsistencyInRatesHierarchyInPeriod(): boolean {
    if (!this.args.column.isPastDay) {
      if (!this.args.row?.caratesPerPeriod) {
        return false;
      }

      const ratesOfTheDay: Carate[] | undefined =
        this.args.row?.caratesPerPeriod?.[this.args.column.id];

      if (ratesOfTheDay && ratesOfTheDay.length === 0) {
        return false;
      }

      return (ratesOfTheDay as Carate[]).some(checkInconsistencyInRate);
    }

    return false;
  }

  get hasInteractions(): boolean {
    return (
      !this.access.isPricingAssistantActionOrInputDisabled &&
      !this.args.column.isPastDay
    );
  }

  get hasRecommendation(): boolean {
    return this.recommendation !== undefined && this.recommendation !== null;
  }

  get iconColor(): 'inherit' | 'on-intelligence' | 'on-critical' | 'info' {
    if (this.showException) {
      return 'on-critical';
    }

    if (!this.hasRecommendation && this.hasInconsistencyInRate) {
      return 'info';
    }

    if (this.args.column.isPastDay) {
      return 'inherit';
    }
    if (this.isAutopilotEnabled || this.hasRecommendation) {
      return 'on-intelligence';
    }
    return 'inherit';
  }

  get iconName(): IconName | undefined {
    if (this.showException) {
      return 'circle-error-outline';
    }

    if (!this.args.column.isPastDay) {
      if (!this.hasRecommendation && this.hasInconsistencyInRate) {
        return 'bullet';
      }

      if (this.isAutopilotEnabled) {
        return 'automation';
      }
      if (this.hasRecommendation && this.arrowIconName) {
        return this.arrowIconName;
      }
    }
    return undefined;
  }

  get isAutopilotEnabled(): boolean {
    return Boolean(
      this.carate?.autopilotEnabled || this.recommendation?.autopilotEnabled,
    );
  }

  get isInPushingState(): boolean {
    return (
      this.recommendation?.isBeingPushedOrIsPushed ||
      this.args.column.daysWithActivePricePush.includes(this.args.column.id) ||
      this.pricingAssistantRecommendationActionPoller.datesBeingRefreshed.has(
        this.args.column.id,
      )
    );
  }

  get isValidEditedPrice(): boolean {
    return this.editInputValue !== null && this.editInputValue > 0;
  }

  get mainValue(): string {
    if (this.carate) {
      if (this.args.column.isPastDay) {
        return this.carate.hasPmsValue
          ? (this.carate.formattedPmsRate ?? '')
          : this.intl.t('general.missing');
      }
      if (this.recommendation && this.hasRecommendation) {
        if (this.recommendation.overwrittenValue) {
          return this.recommendation.formattedOverwrittenValue ?? '';
        }
        return this.recommendation.formattedRecommendedPmsValue ?? '';
      }
      return this.carate.hasPmsValue
        ? (this.carate.formattedPmsRate ?? '')
        : this.intl.t('general.missing');
    }

    return '';
  }

  get mainValueClass(): string {
    if (this.showException) {
      return '';
    }

    if (
      this.recommendation &&
      this.hasRecommendation &&
      !this.args.column.isPastDay
    ) {
      return styles['intelligence-highlight'];
    }

    return '';
  }

  get popoverId(): string {
    return `${this.args.parentCellId}-popover`;
  }

  get pricingRowActions(): PricingRowActionButton[] {
    // Possible buttons to display in the popover
    const disableAutopilotAction = {
      name: 'disableAutopilot',
      buttonType: 'secondary',
      icon: 'automation-off',
      action: (event: Event) => this.disableAutopilot(event),
      dataTestId: 'td-pms-table-pricing-cell-disable-autopilot-button',
    } as const;
    const enableAutopilotAction = {
      name: 'enableAutopilot',
      buttonType: 'secondary',
      icon: 'automation',
      action: (event: Event) => this.enableAutopilot(event),
      dataTestId: 'td-pms-table-pricing-cell-enable-autopilot-button',
    } as const;
    const triggerEditActionPrimaryButton = {
      name: 'triggerEdit',
      buttonType: 'primary',
      icon: 'edit-pencil',
      action: (event: Event) => this.toggleCellEditMode(event),
      dataTestId: 'td-pms-table-pricing-cell-trigger-edit-button',
    } as const;
    const triggerEditActionSecondaryButton = {
      name: 'triggerEdit',
      buttonType: 'secondary',
      icon: 'edit-pencil',
      action: (event: Event) => this.toggleCellEditMode(event),
      dataTestId: 'td-pms-table-pricing-cell-trigger-edit-button',
    } as const;
    const cancelEditAction = {
      name: 'cancelEdit',
      buttonType: 'secondary',
      icon: 'cross',
      action: (event?: Event) => this.closePopover(event),
      dataTestId: 'td-pms-table-pricing-cell-cancel-edit-button',
    } as const;
    const sendPriceAction = {
      name: 'send',
      buttonType: 'primary',
      icon: 'send',
      action: (event: Event) => this.sendPrice(event),
      dataTestId: 'td-pms-table-pricing-cell-send-recommendation',
      disabled: !this.isValidEditedPrice,
    } as const;
    const undoPriceAction = {
      name: 'undo',
      buttonType: 'secondary',
      icon: 'undo',
      action: (event: Event) => this.toggleCellUndoMode(event),
      dataTestId: 'td-pms-table-pricing-cell-undo-change',
      disabled: false,
    } as const;

    if (this.args.isPricingChildRow) {
      if (this.areAutopilotActionsAvailable && this.isAutopilotEnabled) {
        if (this.activeCellMode === PMS_TABLE_PRICE_CELL_MODES.EDIT) {
          return [cancelEditAction, sendPriceAction];
        } else {
          return [triggerEditActionPrimaryButton];
        }
      } else {
        if (this.activeCellMode !== PMS_TABLE_PRICE_CELL_MODES.DEFAULT) {
          return [cancelEditAction, sendPriceAction];
        } else if (this.hasInconsistencyInRate) {
          return [undoPriceAction, triggerEditActionPrimaryButton];
        } else {
          return [triggerEditActionPrimaryButton];
        }
      }
    }

    if (this.areAutopilotActionsAvailable && this.isAutopilotEnabled) {
      if (this.activeCellMode === PMS_TABLE_PRICE_CELL_MODES.EDIT) {
        return [cancelEditAction, sendPriceAction];
      }
      return [disableAutopilotAction, triggerEditActionPrimaryButton];
    }

    if (this.hasRecommendation) {
      if (this.activeCellMode === PMS_TABLE_PRICE_CELL_MODES.DEFAULT) {
        return this.areAutopilotActionsAvailable
          ? [
              enableAutopilotAction,
              triggerEditActionSecondaryButton,
              sendPriceAction,
            ]
          : [triggerEditActionSecondaryButton, sendPriceAction];
      }
      return [cancelEditAction, sendPriceAction];
    }

    if (this.activeCellMode === PMS_TABLE_PRICE_CELL_MODES.DEFAULT) {
      return this.areAutopilotActionsAvailable
        ? [enableAutopilotAction, triggerEditActionPrimaryButton]
        : [triggerEditActionPrimaryButton];
    }
    return [cancelEditAction, sendPriceAction];
  }

  get recommendation(): Carecommendation | null | undefined {
    return this.carate?.carecommendation;
  }

  get secondaryIcon(): 'bullet' | undefined {
    if (this.hasInconsistencyInRate) {
      return 'bullet';
    }

    return undefined;
  }

  get secondaryIconColor(): 'inherit' | 'info' {
    if (this.hasInconsistencyInRate) {
      return 'info';
    }

    return 'inherit';
  }

  get secondaryValue(): string {
    if (!this.args.column.isPastDay && this.hasRecommendation && this.carate) {
      return this.carate.hasPmsValue
        ? (this.carate.formattedPmsRate ?? '')
        : this.intl.t('general.missing');
    }
    return '';
  }

  get showException(): boolean {
    return (
      !this.isInPushingState &&
      this.args.column.daysWithFailedPricePush.includes(this.args.column.id)
    );
  }

  /**
   * Check for inconsistencies in rates hierarchy for the day, in case positive,
   * asks for user for confirmation when activating autopilot for that day.
   *
   * In case rates are consistent, just return a Promise<true>.
   */
  async confirmAutopilotActivationInCaseOfInconsistencies(): Promise<boolean> {
    if (!this.hasInconsistencyInRatesHierarchyInPeriod) {
      return true;
    }

    return await this.alerting.showWarningAlert({
      title: this.intl.t(
        'channel_pricing.alerting.overwrite_pms_values.header',
      ),
      icon: '100x100_sync',
      text: this.intl.t('channel_pricing.alerting.enable_autopilot.body'),
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: this.intl.t(
            'channel_pricing.alerting.enable_autopilot.buttons.confirm',
          ),
        },
      },
    });
  }

  /**
   * Check for inconsistencies in rates hierarchy for the day, in case positive,
   * asks for user for confirmation to override the inconsistencies.
   *
   * In case rates are consistent, just return a Promise<true>.
   */
  async confirmRatesOverrideOfInconsistencies(): Promise<boolean> {
    if (!this.hasInconsistencyInRatesHierarchyInPeriod) {
      return true;
    }

    return await this.alerting.showWarningAlert({
      title: this.intl.t(
        'channel_pricing.alerting.overwrite_pms_values.header',
      ),
      icon: '100x100_sync',
      text: this.intl.t('channel_pricing.alerting.overwrite_pms_values.body'),
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: this.intl.t(
            'channel_pricing.alerting.overwrite_pms_values.buttons.confirm',
          ),
        },
      },
    });
  }

  // SMART DISTRIBUTION TODO: bring this logic to another level, does not look right here.
  rollbackAttributes(): void {
    if (this.carate) {
      this.carate.rollbackAttributes();
    }
    if (this.recommendation) {
      this.recommendation.rollbackAttributes();
    }
  }

  showSuccessToastForPricePush(): void {
    if (!this.carate) {
      return;
    }

    const isBaseRoomPricePush =
      this.args.row.type === PMS_TABLE_ROW_TYPES.PRICING_PARENT_ROW;
    const actionedDay = this.hotelAndMarketService.toLocalTime(
      this.carate.period.day.date.format('YYYY-MM-DD'),
    );
    const formattedActionedDay = actionedDay.format(
      this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
    );

    if (isBaseRoomPricePush) {
      const title = this.intl.t(
        'channel_pricing.successful_channel_prices_send.title',
      );
      this.toast.success(
        this.intl.t('channel_pricing.successful_channel_prices_send.text', {
          htmlSafe: true,
          date: formattedActionedDay,
        }),
        {
          title,
          timeout: TOAST_TIMEOUT_LONG,
          showIcon: true,
        },
      );
    } else {
      const priceSend =
        this.editInputValue !== null
          ? this.editInputValue
          : this.recommendation?.recommendedValue;
      const formattedPrice = this.hotelAndMarketService.formatRate(
        priceSend ?? null,
        true,
        false,
      );
      const title = this.intl.t(
        'channel_pricing.successful_room_price_send.title',
      );
      this.toast.success(
        this.intl.t('channel_pricing.successful_room_price_send.text', {
          htmlSafe: true,
          price: formattedPrice,
          roomType: this.args.row.name,
          date: formattedActionedDay,
        }),
        {
          title,
          timeout: TOAST_TIMEOUT_LONG,
          showIcon: true,
        },
      );
    }
  }

  // reevaluate after pushing, maybe use higher level service or util.
  async updateAutopilotEnabledState(
    isEnabled: boolean,
  ): Promise<{ isSuccess: boolean } | null> {
    if (!this.areAutopilotActionsAvailable) {
      return null;
    }

    // Update state locally to get immediate UI feedback
    if (this.carate) {
      this.carate.autopilotEnabled = isEnabled;

      if (this.recommendation) {
        this.recommendation.autopilotEnabled = isEnabled;
      }

      // Do API request here to update the autopilotEnabled flag
      const result =
        await this.pricingAssistantDataRequestService.saveAndUpdateAutopilotEnabledStateForADayTask.perform(
          this.args.column.id,
          this.carate.autopilotEnabled,
        );

      if (!result.isSuccess) {
        this.rollbackAttributes();
      }

      return result;
    }

    return null;
  }

  @action
  closePopover(event?: Event): void {
    this.popoverElement?.hidePopover();

    if (event) {
      event.stopPropagation();
    }
  }

  @action
  disableAutopilot(event: Event): void {
    void this.updateAutopilotEnabledState(false);

    if (this.carate) {
      trackAutopilotDayToggle(this.usageAnalytics, this.hotelAndMarketService, {
        date: this.carate.period.day.date.toDate(),
        enabled: false,
        eventTrigger: 'pms-table',
        product: 'smart-distribution',
        type: 'pricing',
      });
    }

    this.closePopover(event);
  }

  @action
  async enableAutopilot(event: Event): Promise<void> {
    const confirmOverride =
      await this.confirmAutopilotActivationInCaseOfInconsistencies();

    if (confirmOverride) {
      void this.updateAutopilotEnabledState(true);

      if (this.carate) {
        trackAutopilotDayToggle(
          this.usageAnalytics,
          this.hotelAndMarketService,
          {
            date: this.carate.period.day.date.toDate(),
            enabled: true,
            eventTrigger: 'pms-table',
            product: 'smart-distribution',
            type: 'pricing',
          },
        );
      }
    }

    this.closePopover(event);
  }

  @action
  handleCellClick(event: MouseEvent): void {
    // Staff user debugging: CMD/Meta + click to log cell state
    if (this.access.isStaffUser && event.metaKey) {
      event.stopPropagation();

      logPmsTablePricingCellDebugValues(
        this.args.column.id,
        this.hasRecommendation,
        this.isAutopilotEnabled,
        this.showException,
        this.hasInconsistencyInRate,
        this.carate,
        this.recommendation,
      );
    }
  }

  @action
  onEditInputValueChange(enteredPrice: number | null): void {
    // Add debounced task in-between to avoid directly showing warning when the user has just commenced typing
    void this.debouncedUpdateEditInputValueTask.perform(enteredPrice);
  }

  @action
  onPopoverToggle(event: ToggleEvent): void {
    this.popoverElement =
      event.newState === 'open' ? (event.target as HTMLElement) : null;
    this.isPopoverOpen = event.newState === 'open';

    if (event.newState === 'closed') {
      this.activeCellMode = PMS_TABLE_PRICE_CELL_MODES.DEFAULT;
    }
  }

  @action
  async sendPrice(event: Event): Promise<void> {
    const confirmOverride = await this.confirmRatesOverrideOfInconsistencies();

    // Product wants to be able to track whether users actually still pushed the price after seeing this warning modal or not.
    // The user should only get inside the if statement below if he cancelled in the modal, or closed the modal with an outside click
    // Note: the case where users push the price after the warning modal is tracked as metadata in the separate push tracking event
    if (!confirmOverride) {
      this.usageAnalytics.track('Price Push Dismissed', {
        eventTrigger: 'pms-table',
        product: 'smart-distribution',
        warningShown:
          SEGMENT_METADATA.WARNING_MODALS.ROOMTYPE_HIERARCHY_INCONSISTENCY,
      });

      return;
    }

    const isCustomPricePush =
      this.editInputValue !== null &&
      this.editInputValue !== this.recommendation?.recommendedPmsValue;

    // Disable autopilot for the day if it's enabled
    if (this.carate && this.carate.autopilotEnabled) {
      void this.updateAutopilotEnabledState(false);
    }

    if (isCustomPricePush) {
      if (this.carate && this.editInputValue !== null) {
        void this.pricingAssistantHandleRecommendationActionsService.handleCreateRecommendationActionWithOverwrittenValue(
          this.carate,
          this.recommendation ?? null,
          this.editInputValue,
          false,
          RECOMMENDATION_ACTION_PRICE_TYPES.PMS_PRICE, // indicate that we're actioning PMS prices on this page
        );
      }
    } else if (this.recommendation) {
      void this.pricingAssistantHandleRecommendationActionsService.handleCreateRecommendationAction(
        this.recommendation,
        RECOMMENDATION_ACTION_STATES.ACCEPTED,
        false,
        RECOMMENDATION_ACTION_PRICE_TYPES.PMS_PRICE, // indicate that we're actioning PMS prices on this page
      );
    }

    this.showSuccessToastForPricePush();

    if (this.carate) {
      const pricePushParams = generateRecommendationPushData(
        this.recommendation ?? null,
        this.carate,
      );
      this.usageAnalytics.track('Price Recommendation Pushed Manually', {
        eventTrigger: 'pms-table',
        product: 'smart-distribution',
        ...pricePushParams,
        warningShown: this.hasInconsistencyInRatesHierarchyInPeriod
          ? SEGMENT_METADATA.WARNING_MODALS.ROOMTYPE_HIERARCHY_INCONSISTENCY
          : null,
        daysOffsetFromToday: differenceInDays(
          this.carate.period.day.date.toDate(),
          this.hotelAndMarketService.getLocalTime().toDate(),
        ),
      });
    }

    this.closePopover(event);
  }

  @action
  toggleCellEditMode(event: Event): void {
    this.activeCellMode =
      this.activeCellMode === PMS_TABLE_PRICE_CELL_MODES.EDIT
        ? PMS_TABLE_PRICE_CELL_MODES.DEFAULT
        : PMS_TABLE_PRICE_CELL_MODES.EDIT;
    this.editInputValue = this.editInputValueSameAsFormatted;
    event.stopPropagation();
  }

  @action
  toggleCellUndoMode(event: Event): void {
    this.activeCellMode =
      this.activeCellMode === PMS_TABLE_PRICE_CELL_MODES.UNDO
        ? PMS_TABLE_PRICE_CELL_MODES.DEFAULT
        : PMS_TABLE_PRICE_CELL_MODES.UNDO;

    if (this.activeCellMode === PMS_TABLE_PRICE_CELL_MODES.UNDO) {
      this.usageAnalytics.track('Price Override Undo Started', {
        eventTrigger: 'pms-table',
        product: 'smart-distribution',
      });
    }

    this.editInputValue = this.editInputValueSameAsFormatted;
    event.stopPropagation();
  }

  <template>
    <HotelChannelPricingTablesCellsTableCellWrapper
      @hasInteractions={{this.hasInteractions}}
      @hasOpenCta={{this.isPopoverOpen}}
      @hasRecommendation={{this.hasCellRecommendationBackground}}
      @id={{@parentCellId}}
      @showException={{this.showException}}
      class="edit-cell-popover-button"
      data-test-id="td-pms-table-pricing-cell"
      popovertarget={{this.popoverId}}
      {{on "click" this.handleCellClick}}
    >
      <HotelChannelPricingTablesCellsTableCellBase
        @icon={{this.iconName}}
        @iconColor={{this.iconColor}}
        @isLoading={{this.isInPushingState}}
        @isPriceCell={{true}}
        @main={{this.mainValue}}
        @mainValueClass={{this.mainValueClass}}
        @mainValueHeavyText={{this.hasCellRecommendation}}
        @secondary={{this.secondaryValue}}
        @secondaryIcon={{this.secondaryIcon}}
        @secondaryIconColor={{this.secondaryIconColor}}
        @showException={{this.showException}}
      />

    </HotelChannelPricingTablesCellsTableCellWrapper>

    {{#if this.hasActionPopover}}
      <HotelChannelPricingEditCellPopover
        @onPopoverToggle={{this.onPopoverToggle}}
        @popoverId={{this.popoverId}}
      >
        <div class="flex flex-col items-center gap-200">
          {{#if (notEq this.activeCellMode PMS_TABLE_PRICE_CELL_MODES.DEFAULT)}}
            <PrismNumberInput
              @autofocus={{true}}
              @hasWarnings={{this.editCellNewPriceInputWarningState.hasWarnings}}
              @inlineLabel={{this.hotelAndMarketService.hotel.currencySymbol}}
              @max={{NUMBER_INPUT_MAX_VALUE}}
              @min={{0}}
              @onValueChange={{this.onEditInputValueChange}}
              @removePadding={{true}}
              @value={{this.editInputValue}}
              @warningMessage={{this.editCellNewPriceInputWarningState.warningMessage}}
              data-test-id="td-pms-table-pricing-cell-price-input"
            />
          {{/if}}

          <div class="flex w-full flex-row gap-100">
            {{#each this.pricingRowActions as |pricingRowAction|}}
              <PrismButton
                @buttonType={{pricingRowAction.buttonType}}
                @icon={{pricingRowAction.icon}}
                @isDisabled={{pricingRowAction.disabled}}
                @onClick={{pricingRowAction.action}}
                @size="small"
                data-test-id={{pricingRowAction.dataTestId}}
              />
            {{/each}}
          </div>
        </div>
      </HotelChannelPricingEditCellPopover>
    {{/if}}
  </template>
}
